import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  FeatureFlagControlTagMatch,
  FeatureFlagControlValuesOperation,
  useFeatureFlagControlFlagClauseValuesUpdateMutation,
  useFeatureFlagControlFlagsQuery,
  type FeatureFlagControlSegmentsQuery,
  useFeatureFlagControlSegmentsQuery,
} from '../../../generated';
import { type EntityDescriptor, getEntityDescriptor } from '../entities';
import {
  type ControlledFlag,
  type ControlledSegment,
  type FeatureFlagControlEntity,
} from '../types';
import {
  analyzeTargeting,
  segmentKeysOf,
  usesSegments,
} from '../utils/analyzeTargeting';
import { mapFlag, mapSegment } from '../utils/mapFlags';
import { planToggle, type ToggleWrite } from '../utils/planToggle';
import { resolveServedValue } from '../utils/resolveServedValue';

const LOAD_ERROR_MESSAGE = 'Could not load the features.';
const SAVE_ERROR_MESSAGE = 'Could not change the feature.';
const PENDING_NOTE = 'This change can take a few minutes to apply.';
const GROUPS_UNAVAILABLE = "Can't be changed right now.";

export type FeatureFlagControlRowControl =
  | { kind: 'viewOnly' }
  | { kind: 'locked'; reason: string }
  | { kind: 'editable'; writes: ToggleWrite[] };

export interface FeatureFlagControlRow {
  key: string;
  name: string;
  description?: string;
  isOn: boolean;
  /** What the entity is served, for flags that are not plain on/off. */
  valueLabel: string;
  isBoolean: boolean;
  control: FeatureFlagControlRowControl;
  notes: string[];
}

export type FeatureFlagControlSaveResult =
  | { kind: 'success'; isOn: boolean }
  | { kind: 'error'; message: string };

export interface UseFeatureFlagControlParams {
  entity: FeatureFlagControlEntity;
  /** Flags must carry every one of these tags to be listed. */
  tags: string[];
  /** Flags carrying any of these tags are left out. */
  excludeTags?: string[];
  /** Flags with this tag are shown but can't be changed. */
  readTag: string;
  /** Flags with this tag can be switched for the entity. */
  writeTag: string;
}

interface UseFeatureFlagControlResult {
  rows: FeatureFlagControlRow[];
  isLoading: boolean;
  errorMessage?: string;
  hasNoAccess: boolean;
  isProduction: boolean;
  savingKey?: string;
  setFlag: (
    key: string,
    isOn: boolean
  ) => Promise<FeatureFlagControlSaveResult>;
}

const isOnFor = (
  flag: ControlledFlag,
  servedOnByRule: boolean,
  fallthroughIsOn: boolean
): boolean =>
  flag.evaluation?.isOn ??
  (flag.enabled && (servedOnByRule || fallthroughIsOn));

const buildRow = ({
  flag,
  segments,
  segmentsUnavailable,
  descriptor,
  entityId,
  writeTag,
}: {
  flag: ControlledFlag;
  segments: ControlledSegment[];
  /** The groups could not be read, so their rules can't be resolved. */
  segmentsUnavailable: boolean;
  descriptor: EntityDescriptor;
  entityId: string;
  writeTag: string;
}): FeatureFlagControlRow => {
  const isBlind = segmentsUnavailable && usesSegments(flag);
  const analysis = analyzeTargeting({ flag, descriptor, entityId, segments });
  const servedOnByRule = analysis.handles.some(
    (handle) => handle.present && handle.servesOn
  );
  // The targeting is read live; the evaluation can trail it by a few minutes.
  // Without its groups it can't be resolved, so only the evaluation is used.
  const served = isBlind
    ? undefined
    : resolveServedValue({ flag, descriptor, entityId, segments });
  const isOn =
    served?.isOn ?? isOnFor(flag, servedOnByRule, analysis.fallthroughIsOn);
  const onOffLabel = isOn ? 'On' : 'Off';
  const isPending =
    served !== undefined &&
    flag.evaluation !== undefined &&
    served.isOn !== flag.evaluation.isOn;

  const control = ((): FeatureFlagControlRowControl => {
    if (!flag.tags.includes(writeTag)) return { kind: 'viewOnly' };
    if (isBlind) return { kind: 'locked', reason: GROUPS_UNAVAILABLE };
    if (!flag.isBoolean) {
      return {
        kind: 'locked',
        reason: 'Only features that are on or off can be changed here.',
      };
    }
    return planToggle({
      analysis,
      descriptor,
      isOn,
      reason: served?.reason ?? flag.evaluation?.reason,
    });
  })();

  return {
    key: flag.key,
    name: flag.name,
    description: flag.description,
    isOn,
    valueLabel: flag.isBoolean
      ? onOffLabel
      : (flag.evaluation?.label ?? onOffLabel),
    isBoolean: flag.isBoolean,
    control,
    notes: isPending ? [PENDING_NOTE] : [],
  };
};

/**
 * Describes a refused groups read: its typename and, when the server sent one,
 * its message.
 */
const describeFailure = (
  result:
    | FeatureFlagControlSegmentsQuery['featureFlagControlSegments']
    | undefined
): string | undefined => {
  if (!result) return undefined;
  const message =
    ('message' in result && result.message) ||
    ('serverMessage' in result && result.serverMessage) ||
    undefined;
  return message ? `${result.__typename}: ${message}` : result.__typename;
};

const NO_TAGS: string[] = [];

/**
 * Lists the flags tagged for an entity page with what that entity is served,
 * and switches one of them for that entity only. After a change the flags are
 * read again, so the switch shows what the entity is now served rather than
 * what was asked for.
 */
export const useFeatureFlagControl = ({
  entity,
  tags,
  excludeTags = NO_TAGS,
  readTag,
  writeTag,
}: UseFeatureFlagControlParams): UseFeatureFlagControlResult => {
  const descriptor = getEntityDescriptor(entity.kind);
  const [savingKey, setSavingKey] = useState<string>();

  const {
    data,
    loading,
    error,
    refetch: refetchFlags,
  } = useFeatureFlagControlFlagsQuery({
    variables: {
      input: {
        tags,
        tagMatch: FeatureFlagControlTagMatch.All,
        entity: { kind: entity.kind, id: entity.id },
      },
    },
    skip: !entity.id,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
  });

  const flagsResult = data?.featureFlagControlFlags;
  const configResult = data?.featureFlagControlConfig;

  const flags = useMemo(
    () =>
      flagsResult?.__typename === 'FeatureFlagControlFlags'
        ? flagsResult.flags
            .map(mapFlag)
            .filter(
              (flag) =>
                (flag.tags.includes(readTag) || flag.tags.includes(writeTag)) &&
                !flag.tags.some((tag) => excludeTags.includes(tag))
            )
            .sort((a, b) => a.name.localeCompare(b.name))
        : [],
    [flagsResult, readTag, writeTag, excludeTags]
  );

  const segmentKeys = useMemo(() => segmentKeysOf(flags), [flags]);
  const needsSegments = segmentKeys.length > 0;
  const {
    data: segmentsData,
    loading: segmentsLoading,
    error: segmentsError,
    refetch: refetchSegments,
  } = useFeatureFlagControlSegmentsQuery({
    // Only the groups in use, trimmed to this entity: production groups list
    // too many ids to load in full.
    variables: {
      input: {
        keys: segmentKeys,
        entity: { kind: entity.kind, id: entity.id },
      },
    },
    skip: !needsSegments,
    // The groups come back trimmed to this entity, but their rules and
    // conditions carry provider ids, so a normalised cache would mix one
    // entity's copy into another's. Each read stays in its own result.
    fetchPolicy: 'no-cache',
  });

  const segmentsResult = segmentsData?.featureFlagControlSegments;
  // A failed group read only affects the flags that use groups, so it locks
  // those rows instead of failing the whole list.
  const segmentsUnavailable =
    Boolean(segmentsError) ||
    (segmentsResult !== undefined &&
      segmentsResult.__typename !== 'FeatureFlagControlSegments');
  useEffect(() => {
    if (!segmentsUnavailable) return;
    // eslint-disable-next-line no-console
    console.error(
      'Could not load the feature flag groups:',
      segmentsError?.message ?? describeFailure(segmentsResult)
    );
  }, [segmentsUnavailable, segmentsError, segmentsResult]);
  const segments = useMemo(
    () =>
      segmentsResult?.__typename === 'FeatureFlagControlSegments'
        ? segmentsResult.segments.map(mapSegment)
        : [],
    [segmentsResult]
  );

  const rows = useMemo(
    () =>
      flags.map((flag) =>
        buildRow({
          flag,
          segments,
          segmentsUnavailable,
          descriptor,
          entityId: entity.id,
          writeTag,
        })
      ),
    [flags, segments, segmentsUnavailable, descriptor, entity.id, writeTag]
  );

  const hasNoAccess =
    flagsResult?.__typename === 'AuthorizationFailure' ||
    configResult?.__typename === 'AuthorizationFailure';
  const failedToLoad =
    Boolean(error) ||
    flagsResult?.__typename === 'InputValidationFailure' ||
    flagsResult?.__typename === 'InternalServerError';
  const isProduction =
    configResult?.__typename === 'FeatureFlagControlConfig' &&
    configResult.isProduction;

  const [updateFlagClause] =
    useFeatureFlagControlFlagClauseValuesUpdateMutation();

  /** Applies one write; resolves to false when it is refused. */
  const applyWrite = useCallback(
    async (flagKey: string, write: ToggleWrite): Promise<boolean> => {
      const response = await updateFlagClause({
        variables: {
          input: {
            operation:
              write.operation === 'add'
                ? FeatureFlagControlValuesOperation.Add
                : FeatureFlagControlValuesOperation.Remove,
            confirmProduction: isProduction,
            flagKey,
            ruleId: write.ruleId,
            clauseId: write.clauseId,
            // Clause values are AWSJSON: the id goes over the wire JSON-encoded.
            values: [JSON.stringify(entity.id)],
          },
        },
      });
      const result = response.data?.featureFlagControlFlagClauseValuesUpdate;
      if (result?.__typename === 'FeatureFlagControlFlag') return true;

      // Mutations resolve on GraphQL errors (errorPolicy 'all'), so a rejected
      // call arrives here with no payload rather than as a throw.
      // eslint-disable-next-line no-console
      console.error(
        `Could not change ${flagKey}:`,
        result && 'message' in result
          ? result.message
          : response.errors?.[0]?.message
      );
      return false;
    },
    [entity.id, isProduction, updateFlagClause]
  );

  const setFlag = useCallback(
    async (
      key: string,
      isOn: boolean
    ): Promise<FeatureFlagControlSaveResult> => {
      const row = rows.find((candidate) => candidate.key === key);
      if (!row || row.control.kind !== 'editable' || row.isOn === isOn) {
        return { kind: 'error', message: SAVE_ERROR_MESSAGE };
      }

      setSavingKey(key);
      try {
        // One write at a time, stopping at the first one that is refused.
        const applied = await row.control.writes.reduce<Promise<boolean>>(
          async (previous, write) => (await previous) && applyWrite(key, write),
          Promise.resolve(true)
        );
        if (!applied) return { kind: 'error', message: SAVE_ERROR_MESSAGE };

        const [refetched, refetchedSegments] = await Promise.all([
          refetchFlags(),
          // The change is already saved, so a failed group read only means
          // the result falls back to the groups read before it.
          needsSegments && !segmentsUnavailable
            ? refetchSegments().catch(() => undefined)
            : undefined,
        ]);
        const refetchedFlags =
          refetched.data?.featureFlagControlFlags.__typename ===
          'FeatureFlagControlFlags'
            ? refetched.data.featureFlagControlFlags.flags
            : [];
        const segmentsAfter =
          refetchedSegments?.data?.featureFlagControlSegments.__typename ===
          'FeatureFlagControlSegments'
            ? refetchedSegments.data.featureFlagControlSegments.segments.map(
                mapSegment
              )
            : segments;
        const flagAfter = refetchedFlags
          .map(mapFlag)
          .find((flag) => flag.key === key);
        const servedAfter =
          flagAfter &&
          resolveServedValue({
            flag: flagAfter,
            descriptor,
            entityId: entity.id,
            segments: segmentsAfter,
          });

        return {
          kind: 'success',
          isOn: servedAfter?.isOn ?? flagAfter?.evaluation?.isOn ?? isOn,
        };
      } catch (caught) {
        // eslint-disable-next-line no-console
        console.error(`Could not change ${key}:`, caught);
        return { kind: 'error', message: SAVE_ERROR_MESSAGE };
      } finally {
        setSavingKey(undefined);
      }
    },
    [
      applyWrite,
      descriptor,
      entity.id,
      needsSegments,
      refetchFlags,
      refetchSegments,
      rows,
      segments,
      segmentsUnavailable,
    ]
  );

  return {
    rows,
    isLoading:
      (loading && !data) ||
      (needsSegments && segmentsLoading && !segmentsData && !segmentsError),
    errorMessage: failedToLoad ? LOAD_ERROR_MESSAGE : undefined,
    hasNoAccess,
    isProduction,
    savingKey,
    setFlag,
  };
};
